import React, { useEffect, useState } from 'react';
import { Plus, Wallet, Scissors, Users2, ClipboardList, Download, BarChart3, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { formatApiError } from '../lib/api';
import { PageHeader, Button, Card, Input, Select, Modal, Badge, Th, Td, EmptyState } from '../components/ui';
import { fmtMoney, fmtNum, fmtDate, groupCutLogsByDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import CutterSummaryPrint from '../components/CutterSummaryPrint';
import { downloadElementAsPdf } from '../lib/pdf';

export default function Cutters() {
  const { user } = useAuth();
  const isAdmin = user.role === 'owner' || user.role === 'admin';
  const [tab, setTab] = useState('roster');
  const [cutters, setCutters] = useState([]);
  const [logs, setLogs] = useState([]);
  const [payments, setPayments] = useState([]);
  const [products, setProducts] = useState([]);
  const [logOpen, setLogOpen] = useState(false);
  const [editLog, setEditLog] = useState(null);
  const [pay, setPay] = useState(null);
  const [summary, setSummary] = useState(null);

  const load = () => {
    api.get('/cutters').then((r) => setCutters(r.data));
    api.get('/cutting-logs').then((r) => setLogs(r.data));
    api.get('/cutter-payments').then((r) => setPayments(r.data));
    api.get('/products').then((r) => setProducts(r.data));
  };
  useEffect(() => { load(); }, []);

  const delLog = async (id) => {
    if (!window.confirm('Delete this cutting log?')) return;
    try { await api.delete(`/cutting-logs/${id}`); toast.success('Log deleted'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const fin = (cid) => {
    const earned = logs.filter((l) => l.cutterUserId === cid).reduce((s, l) => s + (l.earnings || 0), 0);
    const paid = payments.filter((p) => p.cutterUserId === cid).reduce((s, p) => s + (p.amount || 0), 0);
    return { earned, paid, pending: earned - paid };
  };

  return (
    <div>
      <PageHeader title="Cutters" subtitle="Roll cutting logs, wages & summaries"
        actions={isAdmin && <Button onClick={() => setLogOpen(true)} data-testid="add-cutting-log-btn"><Plus size={16} /> Log Cutting</Button>} />

      <div className="flex gap-1 mb-4 bg-slate-100 p-1 rounded-md w-fit">
        {[['roster', 'Roster', Users2], ['logs', 'Cutting Logs', ClipboardList]].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`cutter-tab-${k}`}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded transition-colors ${tab === k ? 'bg-white text-[#1E3A8A] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === 'roster' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {cutters.map((c) => {
            const f = fin(c.id);
            return (
              <Card key={c.id} className="p-4" data-testid="cutter-card">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-heading font-bold text-slate-900">{c.name}</p>
                    <p className="text-xs text-slate-400">{c.email}</p>
                  </div>
                  <Badge className="bg-slate-100 border-slate-200 text-slate-600">{fmtMoney(c.rollRate || 0)}/roll</Badge>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-[10px] text-slate-400 uppercase">Earned</p>
                    <p className="text-sm font-semibold num text-slate-800">{fmtNum(f.earned)}</p>
                  </div>
                  <div className="bg-slate-50 rounded p-2">
                    <p className="text-[10px] text-slate-400 uppercase">Paid</p>
                    <p className="text-sm font-semibold num text-emerald-600">{fmtNum(f.paid)}</p>
                  </div>
                  <div className="bg-amber-50 rounded p-2">
                    <p className="text-[10px] text-amber-500 uppercase">Pending</p>
                    <p className="text-sm font-semibold num text-[#B45309]">{fmtNum(f.pending)}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="flex-1" onClick={() => setSummary(c)} data-testid="cutter-summary-btn"><BarChart3 size={14} /> Summary</Button>
                  {isAdmin && <Button size="sm" variant="outline" className="flex-1" onClick={() => setPay({ ...c, pending: f.pending })} data-testid="pay-cutter-btn"><Wallet size={14} /> Pay</Button>}
                </div>
              </Card>
            );
          })}
          {!cutters.length && <EmptyState title="No cutters" description="Add users with the Cutter role from the Users page." icon={Scissors} />}
        </div>
      )}

      {tab === 'logs' && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr><Th>Date</Th><Th>Cutter</Th><Th>Product</Th><Th right>Rolls</Th><Th right>Length (Yards)</Th><Th right>Dozens</Th><Th right>Amount</Th><Th>Added By</Th><Th className="text-right">Actions</Th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((l) => (
                  <tr key={l.id} data-testid="cutting-log-row" className="hover:bg-slate-50/60 transition-colors">
                    <Td>{fmtDate(l.date)}</Td>
                    <Td className="font-medium text-slate-800">{l.cutterName}</Td>
                    <Td className="text-slate-600">{l.productName || '—'}</Td>
                    <Td right className="num font-semibold">{fmtNum(l.rolls)}</Td>
                    <Td right className="num">{fmtNum(l.rollLengthYards || 0)}</Td>
                    <Td right className="num">{fmtNum(l.dozens || 0)}</Td>
                    <Td right className="num font-semibold">{fmtMoney(l.earnings)}</Td>
                    <Td className="text-xs text-slate-500">{l.addedBy}</Td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {isAdmin && <>
                        <button onClick={() => setEditLog(l)} className="text-slate-400 hover:text-blue-600 p-1" data-testid="edit-cutlog-btn"><Pencil size={14} /></button>
                        <button onClick={() => delLog(l.id)} className="text-slate-400 hover:text-rose-600 p-1" data-testid="delete-cutlog-btn"><Trash2 size={14} /></button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!logs.length && <EmptyState title="No cutting logged yet" icon={ClipboardList} />}
        </Card>
      )}

      {logOpen && <CutLogModal cutters={cutters} products={products} onClose={() => setLogOpen(false)} onSaved={() => { setLogOpen(false); load(); }} />}
      {editLog && <CutLogModal cutters={cutters} products={products} log={editLog} onClose={() => setEditLog(null)} onSaved={() => { setEditLog(null); load(); }} />}
      {pay && <CutterPayModal cutter={pay} onClose={() => setPay(null)} onSaved={() => { setPay(null); load(); }} />}
      {summary && <CutterDateSummaryModal cutter={summary} logs={logs.filter((l) => l.cutterUserId === summary.id)} onClose={() => setSummary(null)} />}
    </div>
  );
}

function CutLogModal({ cutters, products, log, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const initLines = log
    ? (log.products?.length ? log.products.map((p) => ({ productId: p.productId, dozens: p.dozens })) : [{ productId: log.productId || products[0]?.id || '', dozens: log.dozens || '' }])
    : [{ productId: products[0]?.id || '', dozens: '' }];
  const [f, setF] = useState(log
    ? { cutterUserId: log.cutterUserId, date: log.date, rolls: log.rolls, rollLengthYards: log.rollLengthYards || '', amount: log.earnings || '' }
    : { cutterUserId: cutters[0]?.id || '', date: today, rolls: '', rollLengthYards: '', amount: '' });
  const [lines, setLines] = useState(initLines);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => idx === i ? { ...l, ...patch } : l));
  const totalDz = lines.reduce((s, l) => s + (Number(l.dozens) || 0), 0);
  const save = async () => {
    if (!f.cutterUserId) { toast.error('Select a cutter'); return; }
    if (!Number(f.rolls)) { toast.error('Enter rolls cut'); return; }
    const prods = lines.filter((l) => l.productId && Number(l.dozens) > 0)
      .map((l) => ({ productId: l.productId, dozens: Number(l.dozens) }));
    const payload = {
      cutterUserId: f.cutterUserId, date: f.date, rolls: Number(f.rolls),
      rollLengthYards: Number(f.rollLengthYards) || 0, amount: Number(f.amount) || 0,
      products: prods,
    };
    try {
      if (log) await api.put(`/cutting-logs/${log.id}`, payload);
      else await api.post('/cutting-logs', payload);
      toast.success(log ? 'Log updated' : 'Cutting logged'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={log ? `Edit Cutting · ${log.cutterName}` : 'Log Cutting'} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-cutting-log-btn">Save</Button></>}>
      <div className="space-y-4">
        <Select label="Cutter" value={f.cutterUserId} disabled={!!log} onChange={(e) => set('cutterUserId', e.target.value)} data-testid="cutlog-cutter">
          {cutters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Input label="Date" type="date" value={f.date} max={today} onChange={(e) => set('date', e.target.value)} data-testid="cutlog-date" />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Rolls cut" type="number" step="0.5" value={f.rolls} onChange={(e) => set('rolls', e.target.value)} data-testid="cutlog-rolls" />
          <Input label="Roll length (Yards)" type="number" step="0.5" value={f.rollLengthYards} onChange={(e) => set('rollLengthYards', e.target.value)} data-testid="cutlog-yards" />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-600 uppercase">Products cut from these rolls</span>
            <Button size="sm" variant="outline" onClick={() => setLines((p) => [...p, { productId: products[0]?.id || '', dozens: '' }])} data-testid="cutlog-add-product-btn"><Plus size={13} /> Product</Button>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2 items-end" data-testid="cutlog-product-line">
              <div className="col-span-7">
                <Select value={l.productId} onChange={(e) => setLine(i, { productId: e.target.value })} data-testid="cutlog-product">
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </Select>
              </div>
              <div className="col-span-4"><Input type="number" step="0.5" placeholder="Dozens" value={l.dozens} onChange={(e) => setLine(i, { dozens: e.target.value })} data-testid="cutlog-dozens" /></div>
              <div className="col-span-1 pb-2 text-right">
                {lines.length > 1 && <button onClick={() => setLines((p) => p.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-rose-600"><Trash2 size={14} /></button>}
              </div>
            </div>
          ))}
          <p className="text-[11px] text-slate-400">Total dozens: <span className="num font-semibold">{totalDz}</span></p>
        </div>
        <Input label="Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="cutlog-amount" />
      </div>
    </Modal>
  );
}

function CutterPayModal({ cutter, onClose, onSaved }) {
  const [f, setF] = useState({ amount: Math.max(0, cutter.pending || 0), notes: '' });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try {
      await api.post('/cutter-payments', { cutterUserId: cutter.id, amount: Number(f.amount), notes: f.notes });
      toast.success('Payment recorded'); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Modal open title={`Pay ${cutter.name}`} onClose={onClose} size="sm"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="save-cutter-payment-btn">Record Payment</Button></>}>
      <p className="text-sm text-slate-500 mb-4">Pending balance: <span className="font-semibold num text-[#B45309]">{fmtMoney(cutter.pending)}</span></p>
      <div className="space-y-4">
        <Input label="Amount (KWD)" type="number" step="0.001" value={f.amount} onChange={(e) => set('amount', e.target.value)} data-testid="cutter-payment-amount" />
        <Input label="Notes" value={f.notes} onChange={(e) => set('notes', e.target.value)} />
      </div>
    </Modal>
  );
}

export function CutterDateSummaryModal({ cutter, logs, onClose }) {
  const [month, setMonth] = useState('');
  const [company, setCompany] = useState(null);
  const [downloading, setDownloading] = useState(false);
  useEffect(() => { api.get('/settings').then((r) => setCompany(r.data?.company)).catch(() => {}); }, []);

  const filtered = month ? logs.filter((l) => (l.date || '').startsWith(month)) : logs;
  const rows = groupCutLogsByDate(filtered);
  const totals = rows.reduce((a, r) => ({ rolls: a.rolls + r.rolls, yards: a.yards + r.yards, dozens: a.dozens + r.dozens, amount: a.amount + r.amount }), { rolls: 0, yards: 0, dozens: 0, amount: 0 });
  const periodLabel = month ? new Date(`${month}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }) : 'All Time';

  const download = async () => {
    setDownloading(true);
    try {
      const name = cutter.name.replace(/[^\w\u0600-\u06FF\s-]/g, '').trim().replace(/\s+/g, '-');
      await downloadElementAsPdf(document.getElementById('cutter-summary-print'), `${name}-Cutting-${month || 'All'}.pdf`);
    } catch { toast.error('Could not generate PDF'); }
    setDownloading(false);
  };

  return (
    <Modal open title={`${cutter.name} · Date-wise Cutting`} onClose={onClose} size="lg"
      footer={<>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={download} disabled={downloading} data-testid="download-cutter-pdf-btn"><Download size={16} /> {downloading ? 'Generating…' : 'Download PDF'}</Button>
      </>}>
      <div className="flex items-end gap-3 mb-4">
        <Input label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="cutter-month-input" />
        {month && <Button size="sm" variant="outline" onClick={() => setMonth('')} data-testid="cutter-alltime-btn">All Time</Button>}
        <span className="text-xs text-slate-400 pb-2.5">{periodLabel}</span>
      </div>
      <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
            <tr><Th>Date</Th><Th>Products</Th><Th right>Rolls</Th><Th right>Length (Yards)</Th><Th right>Dozens</Th><Th right>Amount</Th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => (
              <tr key={r.date} data-testid="cutter-summary-row" className="hover:bg-slate-50/60">
                <Td className="font-medium text-slate-800">{fmtDate(r.date)}</Td>
                <Td className="text-xs text-slate-500">{Object.entries(r.products || {}).map(([n, q]) => `${n} (${q} dz)`).join(', ') || '—'}</Td>
                <Td right className="num font-semibold">{fmtNum(r.rolls)}</Td>
                <Td right className="num">{fmtNum(r.yards)}</Td>
                <Td right className="num">{fmtNum(r.dozens)}</Td>
                <Td right className="num">{fmtMoney(r.amount)}</Td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-slate-50 border-t border-slate-200">
              <tr>
                <Td className="font-bold text-slate-800">Total</Td><Td />
                <Td right className="num font-bold">{fmtNum(totals.rolls)}</Td>
                <Td right className="num font-bold">{fmtNum(totals.yards)}</Td>
                <Td right className="num font-bold">{fmtNum(totals.dozens)}</Td>
                <Td right className="num font-bold">{fmtMoney(totals.amount)}</Td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {!rows.length && <EmptyState title="No cutting logged" icon={Scissors} />}
      <div style={{ position: 'fixed', left: '-20000px', top: 0 }}>
        <div id="cutter-summary-print">
          <CutterSummaryPrint cutterName={cutter.name} rows={rows} totals={totals} periodLabel={periodLabel} company={company} />
        </div>
      </div>
    </Modal>
  );
}
